import type { ReactNode } from 'react';
import { useMemo } from 'react';

import type { CampaignButtonOwner, CampaignState } from './CampaignButton';
import type { Size } from './types';
import { cn } from '../cn';
import CampaignButton from './CampaignButton';

export type CadenceData = {
  id: string;
  state: string;
  templateId: string | null;
  name: string;
  interruptMessage: string | null;
  interruptReason: string | null;
};

export type TemplateOption = {
  id: string;
  name: string;
  isSmart?: boolean;
  ownerIcon?: ReactNode;
};

export type OwnerOption = {
  userId: string;
  name: string | null;
  profilePicture: string | null;
};

export type StagingCandidate = {
  candidateId: number;
  status:
    | 'pending'
    | 'researching'
    | 'research_failed'
    | 'gathering_sources'
    | 'synthesizing'
    | 'synthesize_failed'
    | 'filter_failed'
    | 'personalizing'
    | 'personalize_failed'
    | 'rate_limited'
    | 'account_disconnected'
    | 'awaiting_capacity'
    | 'retrying'
    | 'pending_review'
    | 'rejected';
  /**
   * The campaign this candidate is staged into. A switch is a campaign from the
   * user's point of view, so an unresolved switch candidate (no destination
   * template yet) renders as a badge too — `campaignKind` tells the surface how
   * to route clicks / deletes (template routes vs switch routes).
   */
  campaignKind: 'template' | 'switch';
  /** public_id of the template or switch — used for navigation, delete, retry. */
  campaignId: string;
  /** Display name of the template or switch. */
  campaignName: string;
  failureReason: string | null;
  userId: string | null;
  userName: string | null;
  userProfilePicture: string | null;
};

export type CampaignButtonWithCadenceProps = {
  // Data
  cadence?: CadenceData;
  cadenceUserId?: string;
  /**
   * Fallback display name for the cadence owner, used when the owner isn't found in `owners`
   * (e.g. removed from the team). The server already attaches this on the cadence object.
   */
  cadenceOwnerName?: string | null;
  /** Fallback profile picture URL for the cadence owner, same rationale as cadenceOwnerName. */
  cadenceOwnerProfilePicture?: string | null;
  currentUserId: string;
  templates: TemplateOption[];
  owners: OwnerOption[];
  lastUsedTemplateId?: string | null;
  lastUsedTemplateName?: string | null;
  stepError?: string | null;
  /**
   * Non-terminal cadence_template_candidate rows for this contact. Rendered as
   * additional CampaignButton badges before the enroll dropdown so the user can
   * see "this contact is staged into <Template>" between enroll-click and
   * promotion.
   */
  stagingCandidates?: StagingCandidate[];
  onStagingClick?: (candidate: StagingCandidate) => void;
  onStagingDelete?: (candidate: StagingCandidate) => Promise<void>;
  isStagingDeletingCandidateId?: number | null;
  /**
   * Reassign a staging candidate to a new owner. When provided, the staging
   * badge's owner avatar becomes a dropdown (same affordance as an active
   * cadence) — without it the avatar is read-only. Candidates can be reassigned
   * before promotion, not just once they have an active cadence.
   */
  onStagingChangeOwner?: (candidate: StagingCandidate, newUserId: string) => Promise<void>;

  // Mutation callbacks
  onEnroll: (templateId: string) => Promise<void>;
  onPause: (cadenceId: string) => Promise<void>;
  onResume: (cadenceId: string) => Promise<void>;
  onReattach: (cadenceId: string) => Promise<void>;
  onDelete: (cadenceId: string) => Promise<void>;
  onChangeOwner: (cadenceId: string, newUserId: string) => Promise<void>;
  onResearch?: (cadenceId: string) => Promise<void>;
  /**
   * Navigate to the campaign template page for the active cadence. When provided
   * (and the cadence has a templateId) a "View campaign" button appears to the
   * left of the pill. The surface supplies the navigation — dashboard routes
   * in-app, extension opens a new tab.
   */
  onOpenTemplate?: (templateId: string) => void;

  // Loading states
  isPending?: boolean;
  isDeleting?: boolean;

  // UI
  size?: Size;
  className?: string;
  /** Node rendered flush to the left of the active-campaign pill (e.g. a switcher chevron). */
  leadingSegment?: ReactNode;
};

const VALID_CAMPAIGN_STATES = new Set<string>([
  'not-started',
  'active',
  'paused',
  'finished',
  'interrupted',
  'action-required',
  'responded',
  'researching',
  'pending-review',
  'rejected',
  'contact_unavailable',
]);

// States that are "terminal" - contact can start a new campaign
const TERMINAL_CAMPAIGN_STATES = new Set<string>(['contact_unavailable', 'deleted']);

export function cadenceStateToCampaignState(state: string): CampaignState | null {
  if (VALID_CAMPAIGN_STATES.has(state)) return state as CampaignState;
  return null;
}

function stagingStatusToState(status: StagingCandidate['status']): CampaignState {
  // In-flight statuses borrow the cadence 'researching' state so the badge
  // tints/icons read as "in progress" rather than "rejected".
  if (
    status === 'pending' ||
    status === 'researching' ||
    status === 'gathering_sources' ||
    status === 'synthesizing' ||
    status === 'personalizing' ||
    // Blocked statuses are ALIVE and auto-resume — read as "in progress", not
    // "rejected". (They show a per-stage tooltip via stagingTooltip.)
    status === 'rate_limited' ||
    status === 'account_disconnected' ||
    status === 'awaiting_capacity' ||
    status === 'retrying'
  ) {
    return 'researching';
  }
  // Awaiting human approval of the AI draft — there's a dedicated cadence
  // state for this that the badge already knows how to render.
  if (status === 'pending_review') return 'pending-review';
  return 'rejected';
}

/**
 * Tooltip for staging badges. The click navigates to the template staging page
 * (it does NOT pause/restart the cadence — there is no cadence yet), so the
 * default CampaignButton tooltips ("Click to pause", "Click to restart") would
 * be misleading. We override with status-specific copy and append the failure
 * reason when present.
 */
function stagingTooltip(status: StagingCandidate['status'], failureReason: string | null): string {
  const base = (() => {
    switch (status) {
      case 'pending':
        return 'Queued for research';
      case 'researching':
        return 'Researching contact';
      case 'research_failed':
        return 'Research failed';
      case 'gathering_sources':
        return 'Searching the web';
      case 'synthesizing':
        return 'Synthesizing dossier';
      case 'synthesize_failed':
        return 'Dossier failed';
      case 'filter_failed':
        return 'Filtered out';
      case 'personalizing':
        return 'AI drafting message';
      case 'personalize_failed':
        return 'Drafting failed';
      case 'rate_limited':
        return 'Rate limited — will retry automatically';
      case 'account_disconnected':
        return 'LinkedIn disconnected — reconnect to resume';
      case 'awaiting_capacity':
        return 'Waiting for capacity';
      case 'retrying':
        return 'Retrying';
      case 'pending_review':
        return 'Needs review — approve AI draft';
      case 'rejected':
        return 'Rejected';
    }
  })();
  const head = failureReason ? `${base} — ${failureReason}` : base;
  return `${head} · click to view in staging`;
}

export default function CampaignButtonWithCadence({
  cadence,
  cadenceUserId,
  cadenceOwnerName,
  cadenceOwnerProfilePicture,
  currentUserId,
  templates,
  owners: rawOwners,
  lastUsedTemplateId,
  lastUsedTemplateName,
  stepError,
  stagingCandidates,
  onStagingClick,
  onStagingDelete,
  isStagingDeletingCandidateId,
  onStagingChangeOwner,
  onEnroll,
  onPause,
  onResume,
  onReattach,
  onDelete,
  onChangeOwner,
  onResearch,
  onOpenTemplate,
  isPending,
  isDeleting,
  size = 'sm',
  className,
  leadingSegment,
}: CampaignButtonWithCadenceProps) {
  // Build template options for enrollment dropdown
  const templateOptions = useMemo(
    () =>
      templates.map((t) => ({
        value: t.id,
        label: t.name,
        icon: t.ownerIcon,
      })),
    [templates],
  );

  // Build owners list, current user first
  const owners: CampaignButtonOwner[] = useMemo(
    () =>
      [...rawOwners]
        .sort((u) => (u.userId === currentUserId ? -1 : 1))
        .map((u) => ({
          id: u.userId,
          name: u.name,
          profilePicture: u.profilePicture,
        })),
    [rawOwners, currentUserId],
  );

  // Check if cadence is in a terminal state (should be treated as no active cadence)
  const isTerminalState = cadence?.state && TERMINAL_CAMPAIGN_STATES.has(cadence.state);

  // Hide a staging badge for the template that the active cadence already
  // represents — active wins, the staging row will be `promoted` momentarily.
  // A switch candidate's campaignId is a switch public_id, which can never equal
  // a template public_id, so switch badges are never hidden by this filter.
  const activeTemplateId = cadence && !isTerminalState ? cadence.templateId : null;
  const visibleStaging = (stagingCandidates ?? []).filter((c) => c.campaignId !== activeTemplateId);

  const stagingButtons = visibleStaging.map((c) => {
    const state: CampaignState = stagingStatusToState(c.status);
    const ownerFromList = owners.find((o) => o.id === c.userId);
    const stagingOwner: CampaignButtonOwner | undefined =
      ownerFromList ??
      (c.userId != null ? { id: c.userId, name: c.userName, profilePicture: c.userProfilePicture } : undefined);
    // Owner reassignment only exists for template candidates — the switch has no
    // per-candidate change-owner route, so its badge keeps a read-only avatar.
    const canChangeOwner = onStagingChangeOwner != null && c.campaignKind === 'template';
    return (
      <CampaignButton
        key={c.candidateId}
        state={state}
        name={c.campaignName}
        size={size}
        owner={stagingOwner}
        owners={canChangeOwner ? owners : undefined}
        onChangeOwner={canChangeOwner ? (newUserId) => void onStagingChangeOwner(c, newUserId) : undefined}
        onClick={onStagingClick ? () => onStagingClick(c) : undefined}
        onDelete={onStagingDelete ? () => void onStagingDelete(c) : undefined}
        isDeleting={isStagingDeletingCandidateId === c.candidateId}
        tooltipOverride={stagingTooltip(c.status, c.failureReason)}
        disableHoverActionIcon
      />
    );
  });

  // No active cadence OR terminal state: enrollment mode (+ staging badges).
  // While the contact has any staging row, hide the enroll dropdown — adding
  // to a second campaign mid-research would be confusing, and the staging
  // entry has its own delete (trash) affordance to back out.
  if (!cadence || isTerminalState) {
    return (
      <div className={cn('min-w-0 max-w-full', className)}>
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          {stagingButtons}
          {visibleStaging.length === 0 && (
            <CampaignButton
              size={size}
              templateOptions={templateOptions}
              onEnroll={(templateId) => void onEnroll(templateId)}
              lastUsedTemplateId={lastUsedTemplateId || undefined}
              lastUsedTemplateName={lastUsedTemplateName || undefined}
            />
          )}
        </div>
      </div>
    );
  }

  // Convert state
  const campaignState = cadenceStateToCampaignState(cadence.state);
  if (!campaignState) return null;

  // Check if smart campaign
  const isSmart = templates.find((t) => t.id === cadence.templateId)?.isSmart ?? false;

  // Determine click action based on state
  const shouldReattach =
    cadence.state === 'finished' ||
    cadence.state === 'responded' ||
    cadence.state === 'rejected' ||
    cadence.state === 'contact_unavailable';
  const shouldResume =
    cadence.state === 'paused' || cadence.state === 'interrupted' || cadence.state === 'pending-review';

  const handleClick = () => {
    if (shouldReattach) {
      void onReattach(cadence.id);
    } else if (shouldResume) {
      void onResume(cadence.id);
    } else {
      void onPause(cadence.id);
    }
  };

  const handleDelete = () => void onDelete(cadence.id);
  const handleChangeOwner = (newUserId: string) => void onChangeOwner(cadence.id, newUserId);
  const handleResearch = isSmart && onResearch ? () => void onResearch(cadence.id) : undefined;
  const cadenceTemplateId = cadence.templateId;
  const handleOpenTemplate = cadenceTemplateId && onOpenTemplate ? () => onOpenTemplate(cadenceTemplateId) : undefined;

  const ownerFromList = owners.find((o) => o.id === cadenceUserId);
  const owner =
    ownerFromList ??
    (cadenceUserId != null && (cadenceOwnerName != null || cadenceOwnerProfilePicture != null)
      ? { id: cadenceUserId, name: cadenceOwnerName ?? null, profilePicture: cadenceOwnerProfilePicture ?? null }
      : undefined);

  return (
    <div className={cn('min-w-0 max-w-full', className)}>
      <div className="flex items-center gap-2 flex-wrap min-w-0">
        {stagingButtons}
        <CampaignButton
          state={campaignState}
          name={cadence.name}
          size={size}
          leadingSegment={leadingSegment}
          onClick={handleClick}
          onDelete={handleDelete}
          onOpenTemplate={handleOpenTemplate}
          isDeleting={isDeleting}
          owner={owner}
          owners={owners}
          onChangeOwner={handleChangeOwner}
          isPending={isPending}
          error={cadence.interruptMessage || cadence.interruptReason || stepError}
          onResearch={handleResearch}
        />
      </div>
    </div>
  );
}
